import React from 'react';
import { FormControl, Select, MenuItem } from '@mui/material';

interface FilterOption {
  label: string;
  count: number;
}

interface FilterData {
  [filterType: string]: {
    [optionKey: string]: number;
  };
}

interface GenericFilterSelectProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  searchValue: string;
  setSearchValue: (value: string) => void;
  selectedOptions: Record<string, string[]>;
  setSelectedOptions: (options: Record<string, string[]>) => void;
  activeFilterType: string;
  setActiveFilterType: (filterType: string) => void;
  filterData: FilterData;
  onOptionChange: (filterType: string, option: string) => void;
  displayLabel?: string;
}

const GenericFilterSelect: React.FC<GenericFilterSelectProps> = ({
  isOpen,
  setIsOpen,
  searchValue,
  setSearchValue,
  selectedOptions,
  setSelectedOptions,
  activeFilterType,
  setActiveFilterType,
  filterData,
  onOptionChange,
  displayLabel = "Filters",
}) => {
 const capitalizeFirstLetter = (str: string): string => {
  if (!str) return str; // Handle empty string case
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

  const clearAllFilters = () => {
    const clearedOptions: Record<string, string[]> = {};
    Object.keys(filterData).forEach(filterType => {
      clearedOptions[filterType] = [];
    });
    setSelectedOptions(clearedOptions);
    setSearchValue("");
  };

  const hasActiveFilters = () => {
    return Object.values(selectedOptions).some(options => options.length > 0) || searchValue;
  };

  const getTotalSelectedCount = () => {
    return Object.values(selectedOptions).reduce((total, options) => total + options.length, 0);
  };

  const getFilteredOptions = (filterType: string) => {
    const options = filterData[filterType] || {};
    return Object.keys(options).filter(option =>
      searchValue
        ? option.toLowerCase().includes(searchValue.toLowerCase())
        : true
    );
  };

  return (
    <FormControl className="manage_secondary_orders_filter" onClick={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}>
      <span style={{ fontSize: "14px", color: "#6E6969" }}>Filter:</span>
      <Select
        variant="outlined"
        labelId="dropdown_label"
        open={isOpen}
        value={displayLabel}
        onOpen={() => {
          setIsOpen(true);
          setSearchValue("");
        }}
        onClose={() => setIsOpen(false)}
        renderValue={() => (
          <span style={{ color: "#909090", fontSize: "14px" }}>
            {displayLabel} {getTotalSelectedCount() > 0 && `(${getTotalSelectedCount()})`}
          </span>
        )}
        sx={{
          minWidth: "190px",
          marginLeft: "10px",
          "& .MuiSelect-select": {
            height: "32px",
            padding: "6px 12px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            color: "#909090",
            outline: "none",
            border: "none",
          },
          "& .MuiOutlinedInput-root.Mui-focused": {
            outline: "none",
            boxShadow: "none",
          },
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "#DEDEDE",
            borderWidth: "1px",
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "#DEDEDE",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "#DEDEDE",
            borderWidth: "1px",
          },
        }}
        MenuProps={{
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "right",
          },
          transformOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          PaperProps: {
            sx: {
              boxShadow: "none",
              marginTop: "4px",
              border: "1px solid #DEDEDE",
            },
          },
          MenuListProps: {
            sx: {
              paddingTop: "8px",
              paddingBottom: "0",
            },
          },
        }}
      >
        <MenuItem
          key={`filter-${activeFilterType}-${getTotalSelectedCount()}`}
          disableRipple
          style={{
            padding: 0,
            backgroundColor: "transparent",
            outline: "none",
          }}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            setIsOpen(true);
          }}
        >
          <div className="MSO_filter-dropdown-container" onClick={(e) => {e.stopPropagation(); setIsOpen(true);}}>
            <div className="MSO_filter-header">
              <span className="MSO_filter-name">All Filters</span>
              <span
                className={`MSO_filter-clear-all ${
                  hasActiveFilters() && `MSO_filter-clear-all_active`
                }`}
                onClick={clearAllFilters}
              >
                Clear All
              </span>
            </div>
            <div className="MSO_filter_searchBar_container">
              <input
                className="MSO_fiter_searchBar"
                placeholder="Search..."
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                autoFocus
                onBlur={(e) => e.target.focus()}
              />
            </div>

            <div className="MSO_filter-options">
              <div className="MSO_filter-options-left" style={{width: "100px"}}>
                {Object.keys(filterData).map((filterType) => (
                  <div
                    key={filterType}
                    className={`${
                      activeFilterType === filterType && `MSO_filterSelected`
                    }`}
                    onClick={() => setActiveFilterType(filterType)}
                  >
                    {capitalizeFirstLetter(filterType)} ({selectedOptions[filterType]?.length || 0})
                  </div>
                ))}
              </div>
              <div className="MSO_filter-options-right">
                <div className="MSO_filter-option">
                  {getFilteredOptions(activeFilterType).map((option) => (
                    <label
                      key={option}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        color: "#444444",
                        fontSize: "13px",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedOptions[activeFilterType]?.includes(option) || false}
                        onChange={() => onOptionChange(activeFilterType, option)}
                        style={{ borderColor: "#CBCBCB" }}
                      />
                      {`${capitalizeFirstLetter(option)} (${
                        filterData[activeFilterType]?.[option] || 0
                      })`}
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </MenuItem>
      </Select>
    </FormControl>
  );
};

export default GenericFilterSelect;